/**
 * The crane switch, at the top of Other Expenses.
 *
 * Jonathon, 2026-08-26: "for commercial jobs, a crane needs to be an option in
 * other expenses." Everything on that table below this line is a consequence of
 * the estimate — a percentage of revenue, a lift on a metal roof, a fee on a
 * work order. A crane is a decision somebody makes about a building, so it is a
 * switch, and it is off until it is pressed.
 *
 * It computes nothing. The days box holds an override and the amount is worked
 * out by `computeAutoExpenses` alongside every other calculated expense, which
 * is what keeps the number right when the roof is remeasured (rule 3: no
 * arithmetic on money in a component).
 */

import {
  craneDaysForSquares,
  isCommercialJob,
  roofSquares,
  type Estimate,
} from "@roof-hawk/core";

import { t } from "../../i18n";

export interface CraneOptionProps {
  /** The job's type. Residential jobs are not offered a crane at all. */
  jobType: string | null | undefined;
  estimate: Pick<Estimate, "crane" | "crane_days">;
  /** The job's roofs — only to say what the suggested days came from. */
  layouts: readonly { squares?: unknown }[];
  onSave: (patch: Partial<Estimate>) => Promise<void>;
}

export function CraneOption({
  jobType,
  estimate,
  layouts,
  onSave,
}: CraneOptionProps) {
  if (!isCommercialJob(jobType)) return null;

  const squares = roofSquares(layouts);
  const suggested = craneDaysForSquares(squares);

  return (
    <div className="togline craneopt">
      <button
        className={`tog${estimate.crane ? " on" : ""}`}
        aria-label={t("Crane")}
        aria-pressed={estimate.crane === true}
        title={
          estimate.crane
            ? t("Stop budgeting a crane on this estimate")
            : t(
                "Budget a crane — $276 an hour, 8 hours a day minimum, plus 9% fuel and 12% miscellaneous",
              )
        }
        onClick={() => void onSave({ crane: !estimate.crane })}
      />
      <span>{t("Crane")}</span>
      {estimate.crane && (
        <>
          <input
            className="num craneday"
            inputMode="decimal"
            aria-label={t("Days of crane")}
            placeholder={String(suggested)}
            defaultValue={
              estimate.crane_days == null ? "" : String(estimate.crane_days)
            }
            // Saved when the box is left, not on every keystroke — the rule the
            // rest of the builder follows. Typing "15" would otherwise save a
            // one-day crane on the way past and reprice the job twice.
            onBlur={(e) => {
              const typed = e.target.value.trim();
              const days = Number.parseFloat(typed);
              const next =
                typed === "" || !Number.isFinite(days) || days <= 0
                  ? null
                  : days;
              // Blank stays blank rather than being filled in with the
              // suggestion: null is a real answer — "follow the roof" — and
              // writing the number would freeze today's measurement.
              if ((estimate.crane_days ?? null) === next) return;
              void onSave({ crane_days: next });
            }}
          />
          <span className="na-hint">
            {t("days — leave blank to work it out from the roof")}
            {squares > 0
              ? t(" ({squares} squares → {days} days)", {
                  squares: squares.toLocaleString("en-US"),
                  days: suggested,
                })
              : t(" (no roof measured yet)")}
          </span>
        </>
      )}
    </div>
  );
}
